import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TenantDto, TenantSettings } from '@dairy/shared';
import { updateProfile } from '@/auth/session';
import { useSettings } from '@/auth/useSession';
import { toast } from '@/components/toast';
import { errorMessage } from '@/components/errors';
import { apiJson } from '@/lib/api';
import { reconcileAllReminders } from '@/features/events/effects';
import { syncNow } from '@/sync/client';

/** PATCH /tenant (owner, online). Devices pick the settings up on their next pull (PL-6). */
export function useSaveTenant() {
  const { t } = useTranslation();
  const settings = useSettings();
  const [saving, setSaving] = useState(false);
  const saveTenant = async (patch: { name?: string; settings?: Partial<TenantSettings> }): Promise<boolean> => {
    if (!navigator.onLine) {
      toast(t('reports.needsInternet'), 'error');
      return false;
    }
    setSaving(true);
    try {
      const res = await apiJson<TenantDto>('/tenant', {
        method: 'PATCH',
        json: { ...(patch.name ? { name: patch.name } : {}), ...(patch.settings ? { settings: { ...settings, ...patch.settings } } : {}) },
      });
      await updateProfile({ tenantName: res.name, settings: res.settings });
      if (patch.settings) await reconcileAllReminders();
      void syncNow();
      toast(t('common.saved'));
      return true;
    } catch (e) {
      toast(errorMessage(t, e), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { saveTenant, saving };
}
